/**
 * CounterCool carousel: swipeable on phones with buttons and a progress bar, plain CSS row from 768px up.
 * Built on Embla Carousel (assets/embla-carousel.js). Docs: https://www.embla-carousel.com/docs/v8/api/
 *
 * Peek: a second viewport beside the first, holding the same cards one place ahead, so part of the
 * next card always shows. Its own carousel, so its cards can be sized apart from the main ones.
 */
(function () {
  const SLIDE_INSET_PX = 20;

  /**
   * Copy the cards into the peek list, start a second carousel on them, and keep the two in step.
   * @param {HTMLElement} rootEl - The element holding both viewports.
   * @param {HTMLElement} viewportEl - The main carousel's viewport, whose cards are copied.
   * @param {object} mainCarousel - The carousel the peek follows, already started.
   * @param {object} mainOptions - The options that carousel was started with.
   */
  const startPeek = (rootEl, viewportEl, mainCarousel, mainOptions) => {
    const peekViewportEl = rootEl.querySelector('[aura-countercool-carousel-peek]');

    // Do not proceed if this carousel has no peek. Most sections show one card at a time.
    if (!peekViewportEl) {
      return;
    }

    const peekListEl = peekViewportEl.firstElementChild;
    const mainListEl = viewportEl.firstElementChild;

    // Do not proceed unless both lists exist. The code below appends to one and reads the other.
    if (!peekListEl || !mainListEl) {
      return;
    }

    // `data-slides` names the cards when the list also holds other children, such as the dividers.
    const slideSelector = viewportEl.dataset.slides;
    let slideEls = Array.from(mainListEl.children);

    if (slideSelector) {
      slideEls = Array.from(viewportEl.querySelectorAll(slideSelector));
    }

    // Do not proceed if the section rendered no cards. Embla needs at least one card to measure.
    if (slideEls.length === 0) {
      return;
    }

    // Same cards shifted one place: card 2 first, card 1 last so the peek still shows something on
    // the final card. Shifting the list keeps both carousels on the same count and the same index.
    const peekSlideEls = slideEls.slice(1).concat(slideEls[0]);

    // Copied here instead of rendered twice in Liquid; the section hides the copy from screen readers.
    peekSlideEls.forEach((slideEl) => {
      const peekSlideEl = slideEl.cloneNode(true);

      // The Theme Editor marks each card with this attribute. Remove it from the copy so every
      // block is marked on one element only.
      peekSlideEl.removeAttribute('data-shopify-editor-block');
      peekListEl.appendChild(peekSlideEl);
    });

    const peekCarousel = EmblaCarousel(peekViewportEl, mainOptions);

    /**
     * Move one carousel to the card matching the other's.
     * @param {object} leaderCarousel - The carousel that moved.
     * @param {object} followerCarousel - The carousel to bring along.
     */
    const syncCarousel = (leaderCarousel, followerCarousel) => {
      const leaderIndex = leaderCarousel.selectedScrollSnap();

      // Do not proceed if the follower already rests on that card. Embla reports `select` a frame
      // late, so only comparing indexes stops the pair answering each other back and forth.
      if (followerCarousel.selectedScrollSnap() === leaderIndex) {
        return;
      }

      followerCarousel.scrollTo(leaderIndex);
    };

    // `select` fires once the new card is chosen, not every frame, so under a finger the other
    // carousel animates into place when the drag ends.
    mainCarousel.on('select', () => syncCarousel(mainCarousel, peekCarousel));
    peekCarousel.on('select', () => syncCarousel(peekCarousel, mainCarousel));
  };

  /**
   * Say each new card in a live region, so screen reader users hear a change they cannot see.
   * A card with a caption reads the caption. A card without one reads its place, such as "Slide 2 of 3".
   * Live region docs: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
   * @param {HTMLElement} rootEl - The element holding the live region.
   * @param {object} carousel - The carousel to listen to, already started.
   */
  const startAnnouncer = (rootEl, carousel) => {
    const statusEl = rootEl.querySelector('[aura-countercool-carousel-status]');

    // Do not proceed if this carousel has no live region. Nothing else can speak the change.
    if (!statusEl) {
      return;
    }

    const captionEls = Array.from(rootEl.querySelectorAll('.aura-countercool-carousel__caption'));

    // `select` fires when the card changes and not at start, so nothing is read on page load.
    carousel.on('select', () => {
      const activeIndex = carousel.selectedScrollSnap();
      const captionEl = captionEls[activeIndex];

      if (captionEl) {
        // Read each text part on its own, such as the title and then the body, and end each part with
        // a full stop. Plain `textContent` would join them into one run-on sentence.
        const partTexts = Array.from(captionEl.querySelectorAll('*'))
          .filter((partEl) => !partEl.firstElementChild && partEl.textContent.trim() !== '')
          .map((partEl) => partEl.textContent.replace(/\s+/g, ' ').trim().replace(/\.$/, ''));

        statusEl.textContent = partTexts.join('. ');
        return;
      }

      statusEl.textContent = window.accessibilityStrings.carouselSlidePosition
        .replace('[current]', activeIndex + 1)
        .replace('[total]', carousel.scrollSnapList().length);
    });
  };

  /**
   * Start one carousel and keep its buttons and progress bar in step with it.
   * @param {HTMLElement} rootEl - The element holding the viewport and the controls.
   */
  const startCarousel = (rootEl) => {
    const viewportEl = rootEl.querySelector('[aura-countercool-carousel-viewport]');
    const previousButtonEl = rootEl.querySelector('[aura-countercool-carousel-previous]');
    const nextButtonEl = rootEl.querySelector('[aura-countercool-carousel-next]');
    const thumbEl = rootEl.querySelector('[aura-countercool-carousel-thumb]');

    // Do not proceed if any part is missing. The code below assumes all four exist.
    if (!viewportEl || !previousButtonEl || !nextButtonEl || !thumbEl) {
      return;
    }

    // Do not proceed if this carousel already started. Each section's script tag re-runs this file.
    if (rootEl.hasAttribute('data-carousel-started')) {
      return;
    }

    rootEl.setAttribute('data-carousel-started', '');

    // `align` insets every stop to match the first card's padding. `data-carousel-inset` overrides
    // the 20px default for a carousel whose cards sit flush with the edge.
    let insetPx = SLIDE_INSET_PX;

    if (rootEl.dataset.carouselInset !== undefined) {
      insetPx = Number(rootEl.dataset.carouselInset);
    }

    // `data-carousel-contain` drops the stops that scroll past the end, so cards narrower than the
    // screen leave no empty space after the last one. Off by default: a carousel with a peek needs
    // one stop per card, so both stay on the same index.
    // https://www.embla-carousel.com/docs/v8/api/options
    let containScroll = false;

    if (rootEl.dataset.carouselContain !== undefined) {
      containScroll = 'trimSnaps';
    }

    const emblaOptions = {
      // `data-slides` selects the cards when the list holds other children, such as the dividers.
      slides: viewportEl.dataset.slides || null,
      align: () => insetPx,
      containScroll,
      // `active: false` leaves cards untouched from 768px up, matching the CSS breakpoint.
      breakpoints: {
        '(min-width: 768px)': { active: false },
      },
    };

    const embla = EmblaCarousel(viewportEl, emblaOptions);

    /** Size and move the thumb, and switch the buttons on or off. */
    const update = () => {
      const snapCount = embla.scrollSnapList().length;

      // A drag can carry the progress past either end, which would push the thumb out of its bar.
      let progress = embla.scrollProgress();

      if (progress < 0) {
        progress = 0;
      }

      if (progress > 1) {
        progress = 1;
      }

      previousButtonEl.disabled = !embla.canScrollPrev();
      nextButtonEl.disabled = !embla.canScrollNext();

      // The thumb is one snap wide and travels the remaining snaps, ending flush with the right edge.
      thumbEl.style.width = `${100 / snapCount}%`;
      thumbEl.style.transform = `translate3d(${progress * (snapCount - 1) * 100}%, 0px, 0px)`;
    };

    previousButtonEl.addEventListener('click', () => embla.scrollPrev());
    nextButtonEl.addEventListener('click', () => embla.scrollNext());

    // `scroll` fires on every move; `reInit` fires when the snap count can change.
    embla.on('scroll', update);
    embla.on('reInit', update);

    // Set the starting state now; Embla's own `init` event fires a tick later.
    update();

    // `data-carousel-sync` names groups of elements, separated by `|`, such as captions kept
    // outside the slides. Each group has one element per slide and gets `is-active` on its own.
    const syncValue = rootEl.dataset.carouselSync || '';
    const syncSelectors = syncValue.split('|').filter((selector) => selector !== '');

    syncSelectors.forEach((syncSelector) => {
      const syncEls = Array.from(rootEl.querySelectorAll(syncSelector));

      const syncActive = () => {
        const activeIndex = embla.selectedScrollSnap();

        syncEls.forEach((syncEl, index) => {
          syncEl.classList.toggle('is-active', index === activeIndex);
        });
      };

      embla.on('select', syncActive);
      syncActive();
    });

    startAnnouncer(rootEl, embla);

    // Started last so every group above is collected before the peek's copies join the markup,
    // which a group's selector could otherwise match.
    startPeek(rootEl, viewportEl, embla, emblaOptions);
  };

  document.addEventListener('DOMContentLoaded', () => {
    Array.from(document.querySelectorAll('[aura-countercool-carousel]')).forEach(startCarousel);
  });

  // The Theme Editor replaces a section's HTML without firing `DOMContentLoaded` again. Shopify
  // fires `shopify:section:load` on the new section instead, so start its carousels here.
  // https://shopify.dev/docs/storefronts/themes/best-practices/editor/integrate-sections-and-blocks
  document.addEventListener('shopify:section:load', (event) => {
    Array.from(event.target.querySelectorAll('[aura-countercool-carousel]')).forEach(startCarousel);
  });
})();
